import { resetPasswordPayloadSchema } from '@/features/auth/auth-model'
import { errors as apiErrors } from '@/shared/hooks/api/auth'
import { useResetPassword } from '@/shared/hooks/api/auth'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { ZodIssue } from 'zod'
import { AuthLayout } from '@/features/auth/auth-layout'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'

function translateZodIssue(issue: ZodIssue, t: (key: string) => string): string {
  const path = issue.path.join('.')
  if (path === 'password' && issue.message) {
    return t(`resetPassword.fields.password.errors.${issue.message}`)
  }
  if (issue.message === 'passwords_do_not_match') {
    return t('resetPassword.fields.confirmPassword.errors.passwords_do_not_match')
  }
  return issue.message
}

function fieldErrorMessage(
  issues: ZodIssue[],
  field: 'password' | 'confirmPassword',
  t: (key: string) => string
): string | undefined {
  const relevant = issues.filter((issue) => {
    const first = issue.path[0]
    if (field === 'password') {
      return first === 'password'
    }
    return first === 'confirmPassword'
  })
  if (relevant.length === 0) {
    return undefined
  }
  return relevant.map((issue) => translateZodIssue(issue, t)).join(' ')
}

export function ResetPasswordPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [form, setForm] = useState({
    password: '',
    confirmPassword: '',
  })
  const [errors, setErrors] = useState<ZodIssue[]>([])
  const [success, setSuccess] = useState(false)

  const [isInvalidToken, setInvalidToken] = useState(false)
  const [isExpiredToken, setExpiredToken] = useState(false)
  const [isAlreadyUsedToken, setAlreadyUsedToken] = useState(false)

  const { mutateAsync: resetPassword, isPending } = useResetPassword()

  const resetStatus = useCallback(() => {
    setErrors([])
    setInvalidToken(false)
    setExpiredToken(false)
    setAlreadyUsedToken(false)
    setSuccess(false)
  }, [])

  const onSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      resetStatus()

      const validationResult = resetPasswordPayloadSchema.safeParse({
        token: token,
        ...form,
      })

      if (!validationResult.success) {
        setErrors(validationResult.error.issues)
      } else {
        try {
          await resetPassword({
            token: validationResult.data.token,
            password: validationResult.data.password,
          })
          setSuccess(true)
          toast.success(t('resetPassword.notifications.success'))
        } catch (error) {
          if (error instanceof Error) {
            if (error.message === apiErrors.INVALID_PASSWORD_RESET_TOKEN) {
              setInvalidToken(true)
              return
            }
            if (error.message === apiErrors.PASSWORD_RESET_TOKEN_EXPIRED) {
              setExpiredToken(true)
              return
            }
            if (error.message === apiErrors.PASSWORD_RESET_TOKEN_ALREADY_USED) {
              setAlreadyUsedToken(true)
              return
            }
          }
          toast.error(t('resetPassword.notifications.error'))
        }
      }
    },
    [form, resetPassword, t, token, resetStatus]
  )

  const passwordErrorMessage = fieldErrorMessage(errors, 'password', t)
  const confirmPasswordErrorMessage = fieldErrorMessage(errors, 'confirmPassword', t)

  const isSubmitEnabled =
    !success &&
    !!token &&
    form.password.length > 0 &&
    form.confirmPassword.length > 0

  return (
    <AuthLayout>
      <Card className="w-full max-w-md gap-4">
        <CardHeader>
          <CardTitle>{t('resetPassword.title')}</CardTitle>
          <CardDescription>{t('resetPassword.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            {(!token || isInvalidToken || isExpiredToken || isAlreadyUsedToken) && (
              <Alert variant="destructive">
                <AlertDescription className="whitespace-pre-line">
                  {(!token || isInvalidToken) && t('resetPassword.errors.invalidToken')}
                  {isExpiredToken && t('resetPassword.errors.expiredToken')}
                  {isAlreadyUsedToken && (
                    <Trans
                      i18nKey="resetPassword.errors.alreadyUsedToken"
                      components={{
                        Link: (
                          <Link
                            className="text-primary underline underline-offset-4"
                            to="/forgot-password"
                          />
                        ),
                      }}
                    />
                  )}
                </AlertDescription>
              </Alert>
            )}
            {success && (
              <Alert variant="success">
                <AlertDescription className="whitespace-pre-line">
                  <Trans
                    i18nKey="resetPassword.success"
                    components={{
                      Link: (
                        <Link
                          className="text-primary underline underline-offset-4"
                          to="/sign-in"
                        />
                      ),
                    }}
                  />
                </AlertDescription>
              </Alert>
            )}
            <div className="grid gap-2">
              <Label htmlFor="reset-password">{t('resetPassword.fields.password.label')}</Label>
              <Input
                id="reset-password"
                type="password"
                value={form.password}
                disabled={success || !token}
                aria-invalid={passwordErrorMessage ? true : undefined}
                aria-describedby={passwordErrorMessage ? 'reset-password-error' : undefined}
                onChange={(event) => {
                  setForm({ ...form, password: event.target.value })
                  if (errors.length > 0) {
                    setErrors([])
                  }
                }}
                autoComplete="new-password"
              />
              {passwordErrorMessage ? (
                <p id="reset-password-error" className="text-sm text-destructive" role="alert">
                  {passwordErrorMessage}
                </p>
              ) : null}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="reset-password-confirm">
                {t('resetPassword.fields.confirmPassword.label')}
              </Label>
              <Input
                id="reset-password-confirm"
                type="password"
                value={form.confirmPassword}
                disabled={success || !token}
                aria-invalid={confirmPasswordErrorMessage ? true : undefined}
                aria-describedby={
                  confirmPasswordErrorMessage ? 'reset-password-confirm-error' : undefined
                }
                onChange={(event) => {
                  setForm({ ...form, confirmPassword: event.target.value })
                  if (errors.length > 0) {
                    setErrors([])
                  }
                }}
                autoComplete="new-password"
              />
              {confirmPasswordErrorMessage ? (
                <p
                  id="reset-password-confirm-error"
                  className="text-sm text-destructive"
                  role="alert"
                >
                  {confirmPasswordErrorMessage}
                </p>
              ) : null}
            </div>
            <Button type="submit" className="w-full" disabled={!isSubmitEnabled || isPending}>
              {isPending ? <Loader2 className="animate-spin" /> : null}
              {t('resetPassword.actions.submit')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
